import React, { useState } from 'react'

import { Box, Button, Card, CardContent, CardHeader, Tab, Tabs } from '@mui/material'
import CustomerAgency from 'features/agencies/components/CustomerAgency'
import CustomerAudit from 'features/customers/components/audit/CustomerAudit'
import CustomerBilling from 'features/customers/components/billing/CustomerBilling'
import CustomerAddUserButton from 'features/customers/components/CustomerAddUserButton'
import CustomerAgencyEnable from 'features/customers/components/CustomerAgencyEnable'
import CustomerCodeCheck from 'features/customers/components/CustomerCodeCheck'
import CustomerDeleteConfirm from 'features/customers/components/CustomerDeleteConfirm'
import CustomerContactsActivity from 'features/customers/components/contacts/CustomerContactsActivity'
import CustomerDeliveries from 'features/customers/components/deliveries/CustomerDeliveries'
import CustomerDetails from 'features/customers/components/details/CustomerDetails'
import CustomerDomains from 'features/customers/components/domains/CustomerDomains'
import CustomerFeatures from 'features/customers/components/features/CustomerFeatures'
import CustomerMetrics from 'features/customers/components/metrics/CustomerMetrics'
import CustomerPages from 'features/customers/components/pages/CustomerPages'
import CustomerUsers from 'features/customers/components/users/CustomerUsers'
import { type CustomerDetail, useCustomer } from 'features/customers/hooks/useCustomer'
import { useParams } from 'react-router'
import { Link, useNavigate } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { Badge } from 'components/_base'
import LoadingView from 'components/LoadingView'

import EnrichApi from '../components/enrichApi/EnrichApi'
import RevealApi from '../components/revealApi/RevealApi'

const Customer = () => {
  const navigate = useNavigate()

  const { id: customerId, tab = 'details' } = useParams()
  const { data: customer = {} as CustomerDetail, isLoading } = useCustomer(customerId)

  const [modal, setModal] = useState<string | null>()

  const { name, master, deleted_at } = customer

  const handleSetTab = tab => navigate(UrlProvider.getUrl('customer', { id: customerId, tab }))
  const handleCloseModal = () => setModal(null)

  const actions = (
    <>
      <CustomerCodeCheck />
      <CustomerAgencyEnable isAgency={!!master} />
      <CustomerAddUserButton />
      <Link to={UrlProvider.getUrl('customer.edit', { id: customerId })}>
        <Button>Edit</Button>
      </Link>
      <Button color="secondary" onClick={() => setModal('delete')}>
        Delete
      </Button>
    </>
  )

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {name && (
        <Box component="span" sx={{ mr: '1rem' }}>
          {name}
        </Box>
      )}
      {deleted_at && <Badge color="red" text="Deleted" />}
    </Box>
  )

  return (
    <Card>
      <CardHeader action={actions} title={title} />

      <CardContent>
        <Tabs
          onChange={(_e, tab) => handleSetTab(tab)}
          sx={{
            mb: '2rem',
            '.MuiTabs-flexContainer': { justifyContent: 'space-between', '.MuiButtonBase-root': { minWidth: 'auto' } },
          }}
          value={tab}
          variant="fullWidth">
          <Tab label="Details" value="details" />
          <Tab label="Users" value="users" />
          <Tab label="Features" value="features" />
          <Tab label="Domains" value="domains" />
          <Tab label="Pages" value="pages" />
          {!!master && <Tab label="Agency" value="master" />}
          <Tab label="Billing" value="billing" />
          <Tab label="Metrics" value="metrics" />
          <Tab label="Contacts" value="contacts" />
          <Tab label="Deliveries" value="deliveries" />
          <Tab label="Audit" value="audit" />
          <Tab label="API" value="api" />
        </Tabs>

        <LoadingView loading={isLoading}>
          {tab === 'details' && <CustomerDetails customer={customer} />}
          {tab === 'users' && <CustomerUsers />}
          {tab === 'features' && <CustomerFeatures />}
          {tab === 'domains' && <CustomerDomains />}
          {tab === 'pages' && <CustomerPages />}
          {tab === 'master' && <CustomerAgency />}
          {tab === 'billing' && <CustomerBilling />}
          {tab === 'metrics' && <CustomerMetrics />}
          {tab === 'contacts' && <CustomerContactsActivity />}
          {tab === 'deliveries' && <CustomerDeliveries />}
          {tab === 'audit' && <CustomerAudit />}
          {tab === 'api' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5rem' }}>
              <RevealApi />
              <EnrichApi />
            </Box>
          )}
        </LoadingView>
      </CardContent>

      <CustomerDeleteConfirm onClose={handleCloseModal} open={modal === 'delete'} />
    </Card>
  )
}

export default Customer
